/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useEffect, useState } from "react";

import { offEvent, onEvent } from "@adapptio/lib-shared";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { ISchemaFlowNodeModel, ISchemaFlowNodeTypeDefinitionMap, IScope } from "@adapptio/lib-blueprint";

import { ActionModel } from "../../Common/ActionModel";
import { Panel } from "../../../../ui/components/Panel";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ScopeViewer } from "../../../../ui/components/ScopeViewer/ScopeViewer";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { isSelectionItemActionFlowNode } from "../../Common/ActionEditSelection";
import { ActionEditorView } from "../ActionEditorView";

function resolveScope(model: ActionModel): {
	scope: IScope;
	model?: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;
	id?: string;
} {
	const selectedItems = model.getSelection().getItems();

	const globalScope = {
		scope: model.getRuntimeContext().getScope(),
		model: null,
		id: null
	};

	if (selectedItems.length === 0) {
		return globalScope;
	} else if (selectedItems.length === 1) {
		const selectedItem = selectedItems[0];

		if (!isSelectionItemActionFlowNode(selectedItem)) {
			return globalScope;
		}

		const node = model
			.getDesignContext()
			.getNodeById(selectedItems[0].nodeId) as ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;

		if (!node) {
			return null;
		}

		const nodeScope = node.executionResult?.scope;

		if (!nodeScope) {
			return globalScope;
		}

		return {
			scope: {
				...globalScope.scope,
				globalData: {
					...nodeScope.globalData,
					...globalScope.scope.globalData
				},
				localData: nodeScope.localData,
				localType: nodeScope.localType
			},
			model: node,
			id: node.varName.value || null
		};
	} else {
		return null;
	}
}

/*
 * Toolbar
 */
export interface IActionEditorDataExplorerToolbarProps {}

export const ActionEditorDataExplorerToolbar: FC<IActionEditorDataExplorerToolbarProps> = () => {
	// @todo translate
	return (
		<>
			<ToolbarTitle>Data Explorer</ToolbarTitle>
		</>
	);
};

/*
 * Content
 */

export interface IActionEditorDataExplorerContentProps {
	model: ActionModel;
	view: ActionEditorView;
}

export const ActionEditorDataExplorerContent: FC<IActionEditorDataExplorerContentProps> = ({
	model,
	view
}) => {
	const t = useTranslate();

	const [ scope, setScope ] = useState(() => resolveScope(model));
	const [ lastInfo, setLastInfo ] = useState(() => ({
		id: scope?.id
	}));

	useEffect(() => {
		const editSelection = model.getSelection();

		const handleUpdate = () => setScope(resolveScope(model));

		onEvent(editSelection.onChange, handleUpdate);

		return () => {
			offEvent(editSelection.onChange, handleUpdate);
		};
	}, [ model ]);

	useEffect(() => {
		if (scope?.id !== undefined) {
			setLastInfo({
				id: scope.id
			});
		}
	}, [ scope?.id ]);

	useSimpleEvent(view.onExecutionStatusChange, () => setScope(resolveScope(model)), [ view ], true);

	useSimpleEvent(
		scope?.model?.executedEvent,
		() => setScope(resolveScope(model)),
		[ scope?.model ],
		!!scope?.model
	);

	useSimpleEvent(model.onScopeChange, () => setScope(resolveScope(model)));

	const baseClass = BEM("edt-action-editor-data-explorer");
	const nodeType = scope?.model ? scope.model.schema.opts.nodeTypes[scope.model.type] : null;

	let noteContent = (
		<>
			Only <strong>global scope</strong> is shown
		</>
	);

	// console.log(scope?.model);

	if (scope?.model) {
		// @todo Translate
		noteContent = (
			<>
				Showing data as seen by node:{" "}
				<strong>
					{t("editor", nodeType.label)} {lastInfo.id ? `(${lastInfo.id})` : ""}
				</strong>
			</>
		);
	}

	return (
		<Panel scroll={false} className={baseClass.toClassName()}>
			<div className={baseClass.element("tree").toClassName()}>
				{
					<ScopeViewer
						sort
						scope={scope?.scope}
						// @todo Translate
						noScopeNote={"Run action to inspect node's data."}
					/>
				}
			</div>
			<div className={baseClass.element("note", "show").toClassName()}>
				<div className={baseClass.element("note-inner").toClassName()}>{noteContent}</div>
			</div>
		</Panel>
	);
};
